/**
 * components/team/RoleEditorDialog.js
 * Create, edit or read one role. Shared by the organizer and vendor team
 * pages, so "a role is a named set of permissions" is explained once.
 *
 *   role          null to create, a role from the catalogue to edit or view
 *   permissions   the permission catalogue
 *   grantableKeys what the person editing holds; nothing else can go in a role
 *   lockedKeys    permissions every role carries (shown ticked and fixed)
 *   canEdit       false renders the role read-only
 *   onSubmit      ({ key, label, description, permissions }) for create/save
 *   onDelete      shown for custom roles when given
 */
import React, { useMemo, useState } from "react";
import { AppButton, AppDialog } from "../ui/index.js";
import PermissionMatrix from "./PermissionMatrix.js";
import { Alert } from "./TeamPrimitives.js";
import { roleKeyFromName } from "./roleKey.js";

const RoleEditorDialog = ({
  role,
  permissions,
  grantableKeys,
  lockedKeys = [],
  canEdit = true,
  busy,
  error,
  onClose,
  onSubmit,
  onDelete,
}) => {
  const isNew = !role;
  const readOnly = Boolean(role?.builtin) || !canEdit;
  const [label, setLabel] = useState(role?.label || "");
  const [description, setDescription] = useState(role?.description || "");
  const [selected, setSelected] = useState(() => new Set([...(role?.permissions || []), ...lockedKeys]));

  const derivedKey = isNew ? roleKeyFromName(label) : role.key;
  const memberCount = role?.memberCount || 0;

  const toggle = (k) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });

  // Anything the editor does not hold cannot be put in a role; the server
  // refuses it, so it is not offered. Already-selected keys stay visible.
  const offered = useMemo(
    () => (readOnly
      ? permissions
      : permissions.filter((p) => grantableKeys.has(p.key) || selected.has(p.key) || lockedKeys.includes(p.key))),
    [readOnly, permissions, grantableKeys, selected, lockedKeys]
  );

  const title = readOnly ? role.label : isNew ? "New role" : `Edit ${role.label}`;
  const subtitle = readOnly
    ? role?.builtin
      ? "Built-in role. To allow something different, create your own role."
      : "You can see this role but not change it."
    : "A role is a named set of permissions. Give it a clear name, then tick what it allows.";

  const canSave = !readOnly && label.trim().length >= 2 && Boolean(derivedKey) && !busy;
  const submit = () =>
    canSave && onSubmit({ key: derivedKey, label: label.trim(), description: description.trim(), permissions: [...selected] });

  return (
    <AppDialog
      open
      size="lg"
      title={title}
      subtitle={subtitle}
      busy={busy}
      onClose={onClose}
      footer={
        <>
          {!readOnly && !isNew && onDelete ? (
            <span className="lk-dialog__footer-start">
              <AppButton variant="danger" icon="fa fa-trash" onClick={onDelete} disabled={busy}>
                Delete role
              </AppButton>
            </span>
          ) : null}
          <AppButton variant="quiet" onClick={onClose} disabled={busy}>
            {readOnly ? "Close" : "Cancel"}
          </AppButton>
          {!readOnly && (
            <AppButton onClick={submit} disabled={!canSave} loading={busy}>
              {isNew ? "Create role" : "Save role"}
            </AppButton>
          )}
        </>
      }
    >
      <Alert type="error" msg={error} />
      {readOnly && role?.description ? <p style={{ color: "#475569" }}>{role.description}</p> : null}
      {!readOnly && (
        <>
          <div className="lk-field-grid">
            <div className="lk-field">
              <label className="lk-field__label" htmlFor="role-editor-name">Role name</label>
              <input
                id="role-editor-name"
                className="lk-input"
                value={label}
                maxLength={60}
                placeholder="e.g. Night cashier"
                onChange={(e) => setLabel(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && submit()}
                data-autofocus
              />
              <span className="lk-field__hint">
                {isNew
                  ? derivedKey
                    ? `Saved as ${derivedKey}.`
                    : "Start with a letter. Letters, digits and spaces."
                  : `Key ${role.key} stays the same when you rename it.`}
              </span>
            </div>
            <div className="lk-field">
              <label className="lk-field__label" htmlFor="role-editor-desc">What this person does</label>
              <input
                id="role-editor-desc"
                className="lk-input"
                value={description}
                maxLength={300}
                placeholder="e.g. Runs the till and answers quotes after 8pm"
                onChange={(e) => setDescription(e.target.value)}
              />
              <span className="lk-field__hint">Shown under the role name when choosing a role.</span>
            </div>
          </div>
          {memberCount > 0 && (
            <div className="lk-note lk-note--info" style={{ marginBottom: 14 }}>
              <i className="fa fa-users" aria-hidden="true" style={{ marginTop: 2 }} />
              <span>
                {memberCount} {memberCount === 1 ? "person has" : "people have"} this role. Changes apply to
                {memberCount === 1 ? " them" : " all of them"} as soon as you save.
              </span>
            </div>
          )}
        </>
      )}
      <div className="lk-field__label" style={{ margin: "4px 0 10px" }}>
        Permissions ({selected.size} {readOnly ? "allowed" : "selected"})
      </div>
      <PermissionMatrix
        permissions={offered}
        selected={selected}
        onToggle={readOnly ? undefined : toggle}
        readOnly={readOnly}
        lockedKeys={readOnly ? [] : lockedKeys}
      />
    </AppDialog>
  );
};

export default RoleEditorDialog;
